import { ModuleRendering, SlotRendering, getModuleSpotLayoutMode, isSlotRenderingRenderable } from '@frontend/common-types';
import React from 'react';

import { Slot } from '../slot/slot.component';
import { useModuleController } from './module.controller';

interface ModuleProps<TModule extends ModuleRendering = ModuleRendering, TSlot extends SlotRendering = SlotRendering> {
    module: TModule;
    scale: number;
    offsetX: number;
    offsetY: number;
    onSlotClick?: (clickedSlot: TSlot) => void;
    showLabels?: boolean;
    onModuleClick?: (module: TModule) => void;
    spotId?: string;
    onModuleExpand?: (moduleId: string) => void;
    isExpanded?: boolean;
    slotHoverElement?: (slot: TSlot) => React.ReactNode;
    getSlotColor?: (slot: TSlot) => string;
    isSlotDisabled?: (slot: TSlot) => boolean;
    slotRenderElement?: (slot: TSlot) => React.ReactNode;
}

export const Module = <TModule extends ModuleRendering = ModuleRendering, TSlot extends SlotRendering = SlotRendering>(props: ModuleProps<TModule, TSlot>) => {
    const {
        module,
        scale,
        offsetX,
        offsetY,
        onSlotClick,
        showLabels = true,
        onModuleClick,
        onModuleExpand,
        isExpanded = false,
        slotHoverElement,
        getSlotColor,
        isSlotDisabled,
        slotRenderElement
    } = props;
    const {
        isCarousel,
        doorCount,
        handleModuleClick,
        handleCarouselClick,
        handleLabelClick,
        moduleLeft,
        moduleWidth,
        moduleHeight,
        moduleTop,
        doorWidth,
        doorHeight,
        doorColumnLeft
    } = useModuleController(module, scale, offsetX, offsetY, onModuleClick, onModuleExpand);

    const renderableSlots = module.slots?.filter(isSlotRenderingRenderable) ?? [];
    // Non-renderable → SpotLayout shows a count notice; omit the geometric box (often at 0,0,0).
    const layoutMode = getModuleSpotLayoutMode(module, { isExpanded, whenNotRenderable: 'hidden' });

    if (layoutMode === 'hidden') {
        return null;
    }

    if (layoutMode === 'slots-only') {
        return (
            <>
                {renderableSlots.map((slot) => (
                    <Slot
                        key={slot.id}
                        slot={slot as TSlot}
                        scale={scale}
                        offsetX={offsetX}
                        offsetY={offsetY}
                        onSlotClick={onSlotClick}
                        showLabels={showLabels}
                        hoverElement={slotHoverElement}
                        getSlotColor={getSlotColor}
                        isDisabled={isSlotDisabled?.(slot as TSlot) ?? false}
                        renderElement={slotRenderElement}
                    />
                ))}
            </>
        );
    }

    return (
        <>
            {showLabels && (
                <div
                    className={`absolute ${!isCarousel ? 'pointer-events-auto cursor-pointer' : 'pointer-events-none'}`}
                    style={{
                        left: `${moduleLeft + moduleWidth / 2 - 20}px`,
                        top: `${moduleTop - 35}px`
                    }}
                    onClick={!isCarousel ? handleLabelClick : undefined}>
                    <span className='text-foreground-default text-sm font-semibold'>{module.index}</span>
                </div>
            )}

            {isCarousel ? (
                <>
                    <div
                        data-testid='module-box'
                        className='bg-brand-secondary absolute flex cursor-pointer items-center justify-center'
                        style={{
                            left: `${moduleLeft}px`,
                            top: `${moduleTop}px`,
                            width: `${moduleWidth}px`,
                            height: `${moduleHeight}px`
                        }}
                        onClick={handleCarouselClick}
                    />

                    <div
                        className='absolute flex h-72 flex-col gap-1'
                        style={{
                            left: `${doorColumnLeft}px`,
                            top: `${moduleTop + (moduleHeight - 288) / 2}px`
                        }}>
                        {Array.from({ length: doorCount }).map((_, index) => {
                            return (
                                <div
                                    key={`door-${index}`}
                                    className='border-background-default flex items-end border-2'
                                    style={{
                                        width: `${doorWidth}px`,
                                        height: `${doorHeight}px`,
                                        padding: `${8 * scale}px`
                                    }}>
                                    <span className='text-background-surface text-sm font-semibold'>{index + 1}</span>
                                </div>
                            );
                        })}
                    </div>
                </>
            ) : (
                <div
                    data-testid='module-box'
                    className='bg-background-surface absolute rounded'
                    style={{
                        left: `${moduleLeft}px`,
                        top: `${moduleTop}px`,
                        width: `${moduleWidth}px`,
                        height: `${moduleHeight}px`,
                        cursor: onModuleClick ? 'pointer' : 'default'
                    }}
                    onClick={handleModuleClick}
                />
            )}

            {renderableSlots.map((slot) => (
                <Slot
                    key={slot.id}
                    slot={slot as TSlot}
                    scale={scale}
                    offsetX={offsetX}
                    offsetY={offsetY}
                    onSlotClick={onSlotClick}
                    showLabels={showLabels}
                    hoverElement={slotHoverElement}
                    getSlotColor={getSlotColor}
                    isDisabled={isSlotDisabled?.(slot as TSlot) ?? false}
                    renderElement={slotRenderElement}
                />
            ))}
        </>
    );
};
